import type { Zone } from '@seek/audience-zones';
import type { SearchParams } from '@seek/chalice-types';
import type { Locale } from '@seek/melways-sites';

import getCountryFromZone from 'src/config/utils/getCountryFromZone';
import clean from 'src/modules/clean-object';
import { convertToApiQuery } from 'src/modules/seek-jobs-search-query';
import { shouldRequestRelatedSearches } from 'src/modules/seek-jobs-search-query/convertToApiQueryHelper';
import type { Country } from 'src/types/globals';

export const buildApiQuery = ({
  searchQuery,
  country,
  zone,
  userQueryId,
  seekerId,
  solId,
  locale,
  relatedSearchesCount,
  userClientId,
  sessionId,
  baseKeywords,
  source,
  newSince,
  queryHints,
}: {
  searchQuery: SearchParams;
  country: Country;
  zone: Zone;
  userClientId: string;
  sessionId: string;
  userQueryId?: string;
  seekerId?: number;
  solId: string;
  locale: Locale;
  relatedSearchesCount?: number;
  baseKeywords?: string;
  source?: string;
  newSince?: number;
  queryHints?: string;
}) => {
  const tags = searchQuery.tags;
  const analyticsParams = {
    userqueryid: userQueryId || null,
    userid: userClientId || null,
    usersessionid: userClientId || null,
    eventCaptureSessionId: sessionId || null,
  };

  // TODO-ZONES: See if we can update the library so we can pass in Zone
  // instead of country value
  const searchParams = convertToApiQuery({
    country,
    searchQuery,
    zone,
  });

  const isLocationOnlySerp = Boolean(
    !searchQuery.keywords && !searchQuery.classification && searchQuery.where,
  );

  const isLocationAndWorkTypeSerp = Boolean(
    isLocationOnlySerp && searchQuery.worktype,
  );

  const requestFacets = isLocationOnlySerp || isLocationAndWorkTypeSerp;

  const cleanApiQuery = clean({
    newSince,
    siteKey: `${getCountryFromZone(zone)}-Main`,
    sourcesystem: 'houston',
    facets: requestFacets ? 'distinctTitle' : '',
    ...analyticsParams,
    ...{
      ...searchParams,
      baseKeywords: baseKeywords ? baseKeywords : searchParams?.keywords,
      include: searchParams?.include
        ? `${searchParams?.include},pills`
        : `pills`,
    },
    ...{
      tags,
    },
    locale,
    seekerId,
    solId,
    ...(shouldRequestRelatedSearches({ searchParams, zone }) && {
      relatedSearchesCount,
    }),
    ...(source && { source }),
    ...(queryHints && { queryHints }),
  });

  return {
    ...cleanApiQuery,
    baseKeywords: cleanApiQuery?.baseKeywords
      ? cleanApiQuery?.baseKeywords
      : '',
  };
};
